import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { socialCardsApi, SocialCardVerifyResponse, TextTemplate } from '../api/socialCards';
import MultiImageUploader from '../components/social-card/MultiImageUploader';
import TextDropdown from '../components/social-card/TextDropdown';
import PrivacyToggles from '../components/social-card/PrivacyToggles';
import CardPreview from '../components/social-card/CardPreview';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';

const SocialCardGenerator: React.FC = () => {
  const { ticketCode } = useParams<{ ticketCode: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventData, setEventData] = useState<SocialCardVerifyResponse | null>(null);
  const [templates, setTemplates] = useState<TextTemplate[]>([]);
  
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('attending');
  const [customText, setCustomText] = useState<string>('');
  const [generatedCard, setGeneratedCard] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Checkboxes for what to include
  const [includeName, setIncludeName] = useState(true);
  const [includeCompany, setIncludeCompany] = useState(true);
  const [includeRole, setIncludeRole] = useState(true);

  useEffect(() => {
    if (ticketCode) {
      verifyTicketAndLoadData();
    }
  }, [ticketCode]);

  const verifyTicketAndLoadData = async () => {
    try {
      setLoading(true);
      
      // Verify ticket and get event data
      const [verifyResponse, templatesResponse] = await Promise.all([
        socialCardsApi.verifyTicket(ticketCode!),
        socialCardsApi.getTemplates()
      ]);
      
      setEventData(verifyResponse);
      setTemplates(templatesResponse.templates);
      setError(null);
    } catch (err: any) {
      console.error('Error verifying ticket:', err);
      if (err.response?.status === 404) {
        setError('Invalid ticket code or ticket not approved. Please check your ticket and try again.');
      } else {
        setError('Failed to verify ticket. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate card whenever image or text changes
  const generateCard = useCallback(async () => {
    if (!profileImage || !ticketCode) return;
    
    try {
      setIsGenerating(true);
      
      const response = await socialCardsApi.generateCard({
        ticket_code: ticketCode,
        image_data: profileImage,
        company_logo: companyLogo || undefined,
        text_template: selectedTemplate,
        custom_text: selectedTemplate === 'custom' ? customText : undefined,
        include_name: includeName,
        include_company: includeCompany,
        include_role: includeRole
      });
      
      setGeneratedCard(response.card_image);
    } catch (err: any) {
      console.error('Error generating card:', err);
      // Don't show error for auto-generation, just log it
    } finally {
      setIsGenerating(false);
    }
  }, [profileImage, companyLogo, ticketCode, selectedTemplate, customText, includeName, includeCompany, includeRole]);

  // Auto-generate when image or text changes
  useEffect(() => {
    if (profileImage && ticketCode) {
      generateCard();
    }
  }, [profileImage, companyLogo, selectedTemplate, customText, includeName, includeCompany, includeRole, generateCard, ticketCode]);

  const handleImagesUpdated = (profile: string | null, logo: string | null) => {
    setProfileImage(profile);
    setCompanyLogo(logo);
    // Card will auto-generate via useEffect
  };

  const handleTextSelect = (template: string, custom?: string) => {
    setSelectedTemplate(template);
    if (custom !== undefined) {
      setCustomText(custom);
    }
  };

  const handleDownload = async () => {
    // Track download
    if (ticketCode) {
      await socialCardsApi.trackDownload(ticketCode);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your ticket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Social Card</h1>
          <p className="text-gray-600">Share your attendance at {eventData.event_name}</p>
        </div>

        {/* Event Info Bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <span className="text-gray-700">{eventData.event_date}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPinIcon className="h-5 w-5 text-gray-400" />
              <span className="text-gray-700">{eventData.event_venue}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Attendee:</span>
              <span className="font-medium text-gray-700">{eventData.attendee_name}</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Input */}
          <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
            {/* Image Upload Section */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Add Your Images</h2>
              <MultiImageUploader onImagesUpdated={handleImagesUpdated} />
            </div>

            {/* Text Selection and Privacy Options - Show when profile image is uploaded */}
            {profileImage && (
              <>
                <div>
                  <h2 className="text-xl font-semibold mb-4">Customize Your Card</h2>
                  <div className="space-y-6">
                    <TextDropdown
                      templates={templates}
                      eventName={eventData.event_name}
                      onTextSelect={handleTextSelect}
                    />
                    
                    <PrivacyToggles
                      includeName={includeName}
                      includeCompany={includeCompany}
                      includeRole={includeRole}
                      onNameChange={setIncludeName}
                      onCompanyChange={setIncludeCompany}
                      onRoleChange={setIncludeRole}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Column - Preview */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <CardPreview
              cardImage={generatedCard}
              onDownload={handleDownload}
              isGenerating={isGenerating}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialCardGenerator;